
import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import type { MortgageData } from "../../CalculatorTabs";
import type { AutoCalculateOptions, LoanTypeOption } from "../../utils/types";

interface LoanTypeSelectorProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
  onLoanTypeChange: (value: LoanTypeOption) => void;
}

const LoanTypeSelector: React.FC<LoanTypeSelectorProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate,
  onLoanTypeChange
}) => {
  const handleLoanTypeChange = (value: LoanTypeOption) => {
    onLoanTypeChange(value);
  };

  const getLoanTypeDescription = (loanType: LoanTypeOption) => {
    switch (loanType) {
      case 'conventional':
        return {
          description: 'Standard mortgage with flexible terms. 20% down avoids PMI.',
          minDown: '3-5%',
          benefits: ['Flexible terms', 'No upfront funding fees', 'Various down payment options']
        };
      case 'fha':
        return {
          description: 'Government-backed loan with lower down payment requirements.',
          minDown: '3.5%',
          benefits: ['Lower down payment', 'Flexible credit requirements', 'Assumable loans']
        };
      case 'va':
        return {
          description: 'Available to veterans and service members with no down payment.',
          minDown: '0%',
          benefits: ['No down payment', 'No PMI', 'Competitive rates', 'Assumable loans']
        };
      case 'usda':
        return {
          description: 'For rural and suburban areas with no down payment required.',
          minDown: '0%',
          benefits: ['No down payment', 'Below-market rates', 'Rural area focus']
        };
      default:
        return {
          description: '',
          minDown: '',
          benefits: []
        };
    }
  };

  const selectedLoanInfo = getLoanTypeDescription(mortgageData.loanType);
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">Loan Type</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p className="text-sm">
                Your loan type affects down payment requirements, interest rates, and mortgage insurance. 
                Select the program that best fits your situation.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <Select 
        value={mortgageData.loanType}
        onValueChange={handleLoanTypeChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select a loan type" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          <SelectItem value="conventional">
            <div className="flex items-center justify-between w-full">
              <span>Conventional Loan</span>
              <Badge variant="outline" className="ml-2 text-xs">
                3-5% down
              </Badge>
            </div>
          </SelectItem>
          <SelectItem value="fha">
            <div className="flex items-center justify-between w-full">
              <span>FHA Loan</span>
              <Badge variant="outline" className="ml-2 text-xs">
                3.5% down
              </Badge>
            </div>
          </SelectItem>
          <SelectItem value="va">
            <div className="flex items-center justify-between w-full">
              <span>VA Loan</span>
              <Badge variant="secondary" className="ml-2 text-xs">
                0% down
              </Badge>
            </div>
          </SelectItem>
          <SelectItem value="usda">
            <div className="flex items-center justify-between w-full">
              <span>USDA Loan</span>
              <Badge variant="secondary" className="ml-2 text-xs">
                0% down
              </Badge>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Loan Type Information Panel */}
      {mortgageData.loanType && (
        <div className="p-3 bg-muted/30 rounded-lg border">
          <div className="text-sm text-muted-foreground mb-2">
            {selectedLoanInfo.description}
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div>
              <span className="font-medium">Min Down Payment:</span> {selectedLoanInfo.minDown}
            </div>
            {mortgageData.loanType === 'va' && (
              <Badge variant="outline" className="text-xs">
                Veteran/Service Member Required
              </Badge>
            )}
            {mortgageData.loanType === 'usda' && (
              <Badge variant="outline" className="text-xs">
                Rural Area Required
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanTypeSelector;
